import React, { useState, useEffect, useCallback } from 'react';
import { translations, languages } from '../i18n/translations';
import WhatsAppPanel from './WhatsAppPanel';
import MultiLangAdTemplates from './MultiLangAdTemplates';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AdminDashboard({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState('settings');
  const [isSystemActive, setIsSystemActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Language
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('language') || 'ar';
  });
  const t = translations[lang] || translations.en;
  const currentLang = languages.find(l => l.code === lang) || languages[0];

  // Settings
  const [settings, setSettings] = useState({
    senderEmail: '',
    senderPassword: '',
    senderHost: 'smtp.gmail.com',
    senderPort: 587,
    senderName: 'Email System',
    receiverEmail: '',
    receiverPassword: '',
    receiverHost: 'imap.gmail.com',
    receiverPort: 993,
    campaignDurationDays: 7,
    welcomeEmailEnabled: false,
    welcomeEmailSubject: '',
    welcomeEmailBody: '',
    dailyAdTemplates: []
  });

  // Ad Templates
  const [adTemplates, setAdTemplates] = useState([]);

  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    subject: '',
    responseTemplate: ''
  });

  // Leads
  const [leads, setLeads] = useState([]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Unknown Replies
  const [unknownReplies, setUnknownReplies] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Broadcast
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastAttachment, setBroadcastAttachment] = useState(null);
  const [broadcastAttachmentName, setBroadcastAttachmentName] = useState('');

  // Add Lead Modal
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [newLeadData, setNewLeadData] = useState({
    rawMessage: '',
    name: '',
    email: '',
    phone: ''
  });



  // Stats
  const [stats, setStats] = useState({
    totalLeads: 0,
    subscribedLeads: 0,
    repliedLeads: 0,
    pendingEmails: 0,
    sentEmails: 0
  });

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Update language
  useEffect(() => {
    localStorage.setItem('language', lang);
    document.documentElement.dir = currentLang.dir;
    document.documentElement.lang = lang;
  }, [lang, currentLang.dir]);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Extract data from message
  const extractDataFromMessage = (message) => {
    const data = { name: '', email: '', phone: '' };
    
    if (!message) return data;
    
    // Extract name
    const namePatterns = [
      /Name:\s*([^\n\r]+)/i,
      /Kontaktdetails[\s\S]*?Name:\s*([^\n\r]+)/i
    ];
    for (const pattern of namePatterns) {
      const match = message.match(pattern);
      if (match && match[1] && match[1].trim()) {
        data.name = match[1].trim();
        break;
      }
    }
    
    // Extract email
    const emailPatterns = [
      /E-Mail:\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i,
      /Email:\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i,
      /Kontaktdetails[\s\S]*?([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i
    ];
    for (const pattern of emailPatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        data.email = match[1].toLowerCase().trim();
        break;
      }
    }
    
    // Extract phone
    const phonePatterns = [
      /Telefon\s*#?\d*:\s*([+\d\s\-()]+)/i,
      /Phone[^:]*:\s*([+\d\s\-()]+)/i
    ];
    for (const pattern of phonePatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        const phone = match[1].replace(/[^\d+\-\s]/g, '').trim();
        if (phone.length >= 8) {
          data.phone = phone;
          break;
        }
      }
    }
    
    return data;
  };

  // Load Settings
  const loadSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`, { headers });
      const data = await response.json();
      setSettings(prev => ({ ...prev, ...data }));
      setAdTemplates(data.dailyAdTemplates || []);
      setIsSystemActive(data.isActive || false);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }, [token]);

  // Load Keywords
  const loadKeywords = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/keywords`, { headers });
      const data = await response.json();
      setKeywords(data);
    } catch (error) {
      console.error('Error loading keywords:', error);
    }
  }, [token]);

  // Load Leads
  const loadLeads = useCallback(async (page = 1, search = '') => {
    try {
      const url = new URL(`${API_URL}/api/leads`);
      url.searchParams.append('page', page);
      url.searchParams.append('limit', 20);
      if (search) url.searchParams.append('search', search);
      
      const response = await fetch(url, { headers });
      const data = await response.json();
      
      setLeads(data.leads || []);
      setLeadsTotalPages(data.pages || 1);
      setLeadsTotal(data.total || 0);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  }, [token]);

  // Load Unknown Replies
  const loadUnknownReplies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/unknown-replies`, { headers });
      const data = await response.json();
      setUnknownReplies(data.replies || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Error loading unknown replies:', error);
    }
  }, [token]);

  // Load Stats
  const loadStats = useCallback(async () => {
    try {
      const [leadsRes, emailsRes] = await Promise.all([
        fetch(`${API_URL}/api/leads/stats`, { headers }),
        fetch(`${API_URL}/api/scheduled-emails/stats`, { headers })
      ]);
      
      const leadsData = await leadsRes.json();
      const emailsData = await emailsRes.json();
      
      setStats({
        totalLeads: leadsData.total || 0,
        subscribedLeads: leadsData.subscribed || 0,
        repliedLeads: leadsData.hasReplied || 0,
        pendingEmails: emailsData.pendingEmails || 0,
        sentEmails: emailsData.sentEmails || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
    loadKeywords();
    loadLeads();
    loadUnknownReplies();
    loadStats();
  }, [loadSettings, loadKeywords, loadLeads, loadUnknownReplies, loadStats]);

  // Periodic refresh
  useEffect(() => {
    const interval = setInterval(() => {
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadStats]);

  // Save Settings
  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      showNotification(data.message || t.messages.saved);
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Save Ad Templates
  const saveAdTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/ad-templates`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ templates: adTemplates })
      });
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        showNotification('Server not reachable - check if backend is running', 'error');
        return;
      }
      
      const data = await response.json();
      if (response.ok) {
        showNotification(data.message || t.messages.saved);
      } else {
        showNotification(data.message || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification('Connection error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Toggle System
  const toggleSystem = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/toggle`, {
        method: 'POST',
        headers
      });
      const data = await response.json();
      
      if (response.ok) {
        setIsSystemActive(data.isActive);
        showNotification(data.message);
      } else {
        showNotification(data.message, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Add Keyword
  const addKeyword = async () => {
    if (!newKeyword.keyword || !newKeyword.subject || !newKeyword.responseTemplate) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/keywords`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newKeyword)
      });
      
      if (response.ok) {
        setNewKeyword({ keyword: '', subject: '', responseTemplate: '' });
        loadKeywords();
        showNotification(t.messages.keywordAdded);
      } else {
        const data = await response.json();
        showNotification(data.message || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Delete Keyword
  const deleteKeyword = async (id) => {
    if (!window.confirm(t.messages.confirmDelete)) return;
    
    try {
      await fetch(`${API_URL}/api/keywords/${id}`, {
        method: 'DELETE',
        headers
      });
      loadKeywords();
      showNotification(t.messages.keywordDeleted);
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Toggle Keyword
  const toggleKeyword = async (id) => {
    try {
      await fetch(`${API_URL}/api/keywords/${id}/toggle`, {
        method: 'POST',
        headers
      });
      loadKeywords();
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Add Lead (with automatic first ad email)
  const addLead = async () => {
    if (!newLeadData.email) {
      showNotification(t.messages.emailRequired, 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newLeadData.name || 'Unknown',
          email: newLeadData.email.toLowerCase(),
          phone: newLeadData.phone || '',
          sendFirstAdImmediately: true // This flag tells the backend to send first ad immediately
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setShowAddLeadModal(false);
        setNewLeadData({ rawMessage: '', name: '', email: '', phone: '' });
        loadLeads(leadsPage, searchQuery);
        loadStats();
        showNotification(t.messages.clientAdded);
      } else {
        showNotification(data.message || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete Lead
  const deleteLead = async (id) => {
    if (!window.confirm(t.messages.confirmDelete)) return;
    
    try {
      await fetch(`${API_URL}/api/leads/${id}`, {
        method: 'DELETE',
        headers
      });
      loadLeads(leadsPage, searchQuery);
      loadStats();
      showNotification(t.messages.deleted);
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Extend Campaign
  const extendCampaign = async (leadId, additionalDays) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/extend-campaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ additionalDays })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showNotification(data.message);
        loadLeads(leadsPage, searchQuery);
        loadStats();
      } else {
        showNotification(data.message || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Update Ad Template
  const updateAdTemplate = (day, field, value) => {
    setAdTemplates(prev => {
      const updated = [...prev];
      const index = updated.findIndex(t => t.day === day);
      if (index >= 0) {
        updated[index] = { ...updated[index], [field]: value };
      } else {
        updated.push({ day, subject: '', body: '', isActive: true, [field]: value });
      }
      return updated.sort((a, b) => a.day - b.day);
    });
  };

  // Mark Reply as Read
  const markReplyAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/unknown-replies/${id}/read`, {
        method: 'POST',
        headers
      });
      loadUnknownReplies();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Resolve Reply
  const resolveReply = async (id) => {
    try {
      await fetch(`${API_URL}/api/unknown-replies/${id}/resolve`, {
        method: 'POST',
        headers
      });
      loadUnknownReplies();
      showNotification(t.messages.success);
    } catch (error) {
      showNotification(t.messages.error, 'error');
    }
  };

  // Send Broadcast
  const sendBroadcast = async () => {
    if (!broadcastSubject || !broadcastBody) {
      showNotification(t.messages.fillAllFields, 'error');
      return;
    }
    
    const confirmMsg = t.broadcast.confirmMessage.replace('{count}', stats.subscribedLeads);
    if (!window.confirm(confirmMsg)) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('subject', broadcastSubject);
      formData.append('body', broadcastBody);
      if (broadcastAttachment) {
        formData.append('attachment', broadcastAttachment);
      }
      
      const response = await fetch(`${API_URL}/api/leads/broadcast`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      
      if (response.ok) {
        showNotification(data.message);
        setBroadcastSubject('');
        setBroadcastBody('');
        setBroadcastAttachment(null);
        setBroadcastAttachmentName('');
      } else {
        showNotification(data.message || t.messages.error, 'error');
      }
    } catch (error) {
      showNotification(t.messages.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Search Leads
  const handleSearch = (e) => {
    e.preventDefault();
    loadLeads(1, searchQuery);
    setLeadsPage(1);
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" dir={currentLang.dir}>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'
        } text-white font-medium`}>
          {notification.message}
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{t.addLead.title}</h3>
              <button
                onClick={() => setShowAddLeadModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t.addLead.pasteMessage}
                </label>
                <textarea
                  value={newLeadData.rawMessage}
                  onChange={(e) => {
                    const msg = e.target.value;
                    setNewLeadData(prev => ({ ...prev, rawMessage: msg }));
                    if (msg.length > 50) {
                      const extracted = extractDataFromMessage(msg);
                      setNewLeadData(prev => ({
                        ...prev,
                        ...extracted
                      }));
                    }
                  }}
                  rows={8}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                  placeholder={t.addLead.pasteMessagePlaceholder}
                  dir="ltr"
                />
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <p className="text-sm text-slate-400 mb-4">{t.addLead.extractedData}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.addLead.clientName}</label>
                    <input
                      type="text"
                      value={newLeadData.name}
                      onChange={(e) => setNewLeadData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t.addLead.clientName}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.addLead.clientEmail}</label>
                    <input
                      type="email"
                      value={newLeadData.email}
                      onChange={(e) => setNewLeadData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="email@example.com"
                      dir="ltr"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.addLead.clientPhone}</label>
                    <input
                      type="text"
                      value={newLeadData.phone}
                      onChange={(e) => setNewLeadData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1234567890"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-sm text-blue-300">
                  {t.addLead.addNote}
                  <br />{t.addLead.addNoteWelcome}
                  <br />{t.addLead.addNoteFirstAd}
                  <br />{t.addLead.addNoteSchedule} ({settings.campaignDurationDays || 7} {t.adTemplates.days})
                </p>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={addLead}
                  disabled={loading || !newLeadData.email}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50"
                >
                  {loading ? t.addLead.adding : t.addLead.addButton}
                </button>
                <button
                  onClick={() => setShowAddLeadModal(false)}
                  className="px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all"
                >
                  {t.addLead.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-white">{t.dashboard.title}</h1>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <div className="flex gap-1">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => setLang(language.code)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      lang === language.code
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {language.name}
                  </button>
                ))}
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t.dashboard.logout}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-slate-400 text-sm">{t.stats.totalClients}</p>
            <p className="text-2xl font-bold text-white">{stats.totalLeads}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-slate-400 text-sm">{t.stats.subscribed}</p>
            <p className="text-2xl font-bold text-emerald-400">{stats.subscribedLeads}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-slate-400 text-sm">{t.stats.replied}</p>
            <p className="text-2xl font-bold text-blue-400">{stats.repliedLeads}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-slate-400 text-sm">{t.stats.pendingEmails}</p>
            <p className="text-2xl font-bold text-amber-400">{stats.pendingEmails}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-slate-400 text-sm">{t.stats.sentEmails}</p>
            <p className="text-2xl font-bold text-cyan-400">{stats.sentEmails}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'settings', label: t.tabs.settings },
            { id: 'multiLangAds', label: t.tabs.multiLangAds || (lang === 'ar' ? 'اعلانات متعددة اللغات' : 'Multi-Lang Ads') },
            { id: 'adTemplates', label: t.tabs.adTemplates },
            { id: 'keywords', label: t.tabs.keywords },
            { id: 'leads', label: t.tabs.leads },
            { id: 'unknownReplies', label: `${t.tabs.replies} (${unreadCount})` },
            { id: 'broadcast', label: t.tabs.broadcast },
            { id: 'whatsapp', label: t.tabs.whatsapp, icon: 'whatsapp' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status Card */}
        <div className={`mb-8 p-6 rounded-2xl border transition-all duration-500 ${
          isSystemActive 
            ? 'bg-emerald-500/10 border-emerald-500/30' 
            : 'bg-slate-800/50 border-slate-700/50'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-4 h-4 rounded-full ${
                isSystemActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'
              }`}></div>
              <div>
                <h2 className="text-lg font-semibold text-white">{t.system.title}</h2>
                <p className={isSystemActive ? 'text-emerald-400' : 'text-slate-400'}>
                  {isSystemActive ? t.system.running : t.system.stopped}
                </p>
              </div>
            </div>
            <button
              onClick={toggleSystem}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                isSystemActive 
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30' 
                  : 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/25 hover:scale-105'
              }`}
            >
              {isSystemActive ? t.system.stop : t.system.start}
            </button>
          </div>
        </div>

        {/* Multi-Language Ads Tab */}
        {activeTab === 'multiLangAds' && (
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
            <MultiLangAdTemplates token={token} />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sender Email */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
                <h3 className="text-lg font-semibold text-white mb-6">{t.settings.senderEmail}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.senderName}</label>
                    <input
                      type="text"
                      value={settings.senderName}
                      onChange={(e) => setSettings({ ...settings, senderName: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.emailAddress}</label>
                    <input
                      type="email"
                      value={settings.senderEmail}
                      onChange={(e) => setSettings({ ...settings, senderEmail: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.appPassword}</label>
                    <input
                      type="password"
                      value={settings.senderPassword}
                      onChange={(e) => setSettings({ ...settings, senderPassword: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.host}</label>
                      <input
                        type="text"
                        value={settings.senderHost}
                        onChange={(e) => setSettings({ ...settings, senderHost: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.port}</label>
                      <input
                        type="number"
                        value={settings.senderPort}
                        onChange={(e) => setSettings({ ...settings, senderPort: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Receiver Email */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
                <h3 className="text-lg font-semibold text-white mb-6">{t.settings.receiverEmail}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.emailAddress}</label>
                    <input
                      type="email"
                      value={settings.receiverEmail}
                      onChange={(e) => setSettings({ ...settings, receiverEmail: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.appPassword}</label>
                    <input
                      type="password"
                      value={settings.receiverPassword}
                      onChange={(e) => setSettings({ ...settings, receiverPassword: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.host}</label>
                      <input
                        type="text"
                        value={settings.receiverHost}
                        onChange={(e) => setSettings({ ...settings, receiverHost: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t.settings.port}</label>
                      <input
                        type="number"
                        value={settings.receiverPort}
                        onChange={(e) => setSettings({ ...settings, receiverPort: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gender-Based Greetings */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                {lang === 'ar' ? 'كلمات الترحيب حسب الجنس' : lang === 'de' ? 'Geschlechtsbasierte Begrüßungen' : 'Gender-Based Greetings'}
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                {lang === 'ar' 
                  ? 'استخدم {greeting} في الرسائل للترحيب التلقائي حسب جنس العميل' 
                  : lang === 'de' 
                  ? 'Verwenden Sie {greeting} in Nachrichten für automatische geschlechtsbasierte Begrüßungen' 
                  : 'Use {greeting} in messages for automatic gender-based greetings'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { code: 'de', name: 'German', flag: 'DE' },
                  { code: 'en', name: 'English', flag: 'EN' },
                  { code: 'fr', name: 'French', flag: 'FR' },
                  { code: 'es', name: 'Spanish', flag: 'ES' },
                  { code: 'it', name: 'Italian', flag: 'IT' },
                  { code: 'pt', name: 'Portuguese', flag: 'PT' }
                ].map(lang => (
                  <div key={lang.code} className="bg-slate-700/30 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-white text-sm font-medium">{lang.name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">
                          {lang === 'ar' ? 'ذكر' : 'Male'}
                        </label>
                        <input
                          type="text"
                          value={settings.genderGreetings?.[lang.code]?.male || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            genderGreetings: {
                              ...settings.genderGreetings,
                              [lang.code]: {
                                ...settings.genderGreetings?.[lang.code],
                                male: e.target.value
                              }
                            }
                          })}
                          placeholder={lang.code === 'de' ? 'Lieber' : lang.code === 'en' ? 'Dear Mr.' : ''}
                          className="w-full px-2 py-1.5 bg-slate-600/50 border border-slate-500 rounded text-white text-sm"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">
                          {lang === 'ar' ? 'انثى' : 'Female'}
                        </label>
                        <input
                          type="text"
                          value={settings.genderGreetings?.[lang.code]?.female || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            genderGreetings: {
                              ...settings.genderGreetings,
                              [lang.code]: {
                                ...settings.genderGreetings?.[lang.code],
                                female: e.target.value
                              }
                            }
                          })}
                          placeholder={lang.code === 'de' ? 'Liebe' : lang.code === 'en' ? 'Dear Ms.' : ''}
                          className="w-full px-2 py-1.5 bg-slate-600/50 border border-slate-500 rounded text-white text-sm"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Signature */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {lang === 'ar' ? 'توقيع البريد' : lang === 'de' ? 'E-Mail-Signatur' : 'Email Signature'}
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.emailSignatureEnabled || false}
                    onChange={(e) => setSettings({ ...settings, emailSignatureEnabled: e.target.checked })}
                    className="w-5 h-5 rounded bg-slate-600 border-slate-500 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-300">{t.settings.enable}</span>
                </label>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                {lang === 'ar' 
                  ? 'أضف توقيع يظهر في نهاية كل رسالة (يدعم HTML - لوغو، روابط، معلومات الاتصال)' 
                  : lang === 'de' 
                  ? 'Signatur am Ende jeder E-Mail hinzufügen (unterstützt HTML - Logo, Links, Kontaktinfo)' 
                  : 'Add signature at the end of every email (supports HTML - logo, links, contact info)'}
              </p>
              
              <div className="space-y-3">
                <textarea
                  value={settings.emailSignature || ''}
                  onChange={(e) => setSettings({ ...settings, emailSignature: e.target.value })}
                  rows={8}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder={`<div style="border-top: 1px solid #ccc; padding-top: 10px; margin-top: 20px;">
  <img src="https://your-logo-url.com/logo.png" alt="Company Logo" width="150">
  <p><strong>Paul Weber</strong><br>KIA Abteilung Umzug</p>
  <p>📍 Birkenstrasse 21, 8306 Wangen-Brüttisellen</p>
  <p>📞 044 775 66 66</p>
  <p>🌐 <a href="https://www.helvetictransporte.ch">www.helvetictransporte.ch</a></p>
</div>`}
                  dir="ltr"
                />
                
                {settings.emailSignature && (
                  <div className="bg-slate-700/30 rounded-xl p-4">
                    <p className="text-xs text-slate-400 mb-2">
                      {lang === 'ar' ? 'معاينة:' : lang === 'de' ? 'Vorschau:' : 'Preview:'}
                    </p>
                    <div 
                      className="bg-white text-black p-4 rounded-lg text-sm"
                      dangerouslySetInnerHTML={{ __html: settings.emailSignature }}
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={saveSettings}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50"
            >
              {loading ? t.settings.saving : t.settings.saveSettings}
            </button>
          </div>
        )}

        {/* Ad Templates Tab */}
        {activeTab === 'adTemplates' && (
          <div className="space-y-6">
            {/* Campaign Duration */}
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t.adTemplates.campaignDuration}</h3>
              <p className="text-sm text-slate-400 mb-4">{t.adTemplates.campaignDurationDesc}</p>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="text-slate-300">{t.adTemplates.duration}</label>
                <select
                  value={settings.campaignDurationDays || 7}
                  onChange={(e) => setSettings({ ...settings, campaignDurationDays: parseInt(e.target.value) })}
                  className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={7}>7 {t.adTemplates.days} (1 {t.adTemplates.week})</option>
                  <option value={14}>14 {t.adTemplates.days} (2 {t.adTemplates.weeks})</option>
                  <option value={21}>21 {t.adTemplates.days} (3 {t.adTemplates.weeks})</option>
                  <option value={28}>28 {t.adTemplates.days} (4 {t.adTemplates.weeks})</option>
                  <option value={30}>30 {t.adTemplates.days} (1 {t.adTemplates.month})</option>
                  <option value={60}>60 {t.adTemplates.days} (2 {t.adTemplates.months})</option>
                  <option value={90}>90 {t.adTemplates.days} (3 {t.adTemplates.months})</option>
                </select>
              </div>
            </div>

            {/* Note about first ad */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-sm text-emerald-300">{t.adTemplates.firstAdNote}</p>
            </div>

            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">{t.adTemplates.title}</h3>
                <p className="text-sm text-slate-400">{t.adTemplates.templatesRepeat}</p>
              </div>
              
              <div className="space-y-6">
                {[1, 2, 3, 4, 5, 6, 7].map(day => {
                  const template = adTemplates.find(t => t.day === day) || { day, subject: '', body: '', isActive: true };
                  return (
                    <div key={day} className={`bg-slate-700/30 rounded-xl p-4 border ${day === 1 ? 'border-emerald-500/50' : 'border-slate-600/50'}`}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-white font-medium">
                          {t.adTemplates.day} {day}
                          {day === 1 && <span className="ml-2 text-xs text-emerald-400">(First - Sent Immediately)</span>}
                        </h4>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={template.isActive}
                            onChange={(e) => updateAdTemplate(day, 'isActive', e.target.checked)}
                            className="w-5 h-5 rounded bg-slate-600 border-slate-500 text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-300">{t.adTemplates.active}</span>
                        </label>
                      </div>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={template.subject || ''}
                          onChange={(e) => updateAdTemplate(day, 'subject', e.target.value)}
                          className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`${t.settings.subject} - ${t.adTemplates.day} ${day}`}
                        />
                        <textarea
                          value={template.body || ''}
                          onChange={(e) => updateAdTemplate(day, 'body', e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          placeholder={`${t.settings.body} - ${t.adTemplates.day} ${day}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={saveAdTemplates}
                disabled={loading}
                className="w-full mt-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50"
              >
                {loading ? t.settings.saving : t.adTemplates.saveTemplates}
              </button>
            </div>
          </div>
        )}

        {/* Keywords Tab */}
        {activeTab === 'keywords' && (
          <div className="space-y-6">
            {/* Auto-reply notice */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-sm text-emerald-300 font-medium">{t.keywords.autoReplyNote}</p>
            </div>

            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-white mb-2">{t.keywords.title}</h3>
              <p className="text-sm text-slate-400 mb-6">{t.keywords.subtitle}</p>
              
              {/* Add Keyword Form */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <input
                  type="text"
                  value={newKeyword.keyword}
                  onChange={(e) => setNewKeyword({ ...newKeyword, keyword: e.target.value })}
                  className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.keywords.keyword}
                />
                <input
                  type="text"
                  value={newKeyword.subject}
                  onChange={(e) => setNewKeyword({ ...newKeyword, subject: e.target.value })}
                  className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.keywords.replySubject}
                />
                <textarea
                  value={newKeyword.responseTemplate}
                  onChange={(e) => setNewKeyword({ ...newKeyword, responseTemplate: e.target.value })}
                  className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder={t.keywords.replyTemplate}
                  rows={1}
                />
              </div>
              <button
                onClick={addKeyword}
                className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all"
              >
                {t.keywords.add}
              </button>
            </div>

            {/* Keywords List */}
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
              {keywords.length === 0 ? (
                <p className="p-6 text-center text-slate-400">{t.keywords.noKeywords}</p>
              ) : (
                <div className="divide-y divide-slate-700/50">
                  {keywords.map((kw) => (
                    <div key={kw._id} className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                              kw.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
                            }`}>
                              {kw.keyword}
                            </span>
                            <span className="text-xs text-slate-500">
                              {t.keywords.matches}: {kw.totalMatches || 0} | {t.keywords.emailsSent}: {kw.totalEmailsSent || 0}
                            </span>
                          </div>
                          <p className="text-sm text-slate-300 mb-1"><strong>{t.settings.subject}:</strong> {kw.subject}</p>
                          <p className="text-sm text-slate-400 line-clamp-2">{kw.responseTemplate}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleKeyword(kw._id)}
                            className={`p-2 rounded-lg transition-all ${
                              kw.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
                            }`}
                          >
                            {kw.isActive ? '✓' : '○'}
                          </button>
                          <button
                            onClick={() => deleteKeyword(kw._id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t.leads.search}
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all"
                >
                  {t.common.search}
                </button>
              </form>
              <button
                onClick={() => setShowAddLeadModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-xl shadow-lg hover:scale-[1.02] transition-all"
              >
                {t.leads.addClient}
              </button>
            </div>

            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
              {leads.length === 0 ? (
                <p className="p-6 text-center text-slate-400">{t.leads.noClients}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-700/30">
                      <tr>
                        <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.name}</th>
                        <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.email}</th>
                        <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.phone}</th>
                        <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.status}</th>
                        <th className="px-4 py-3 text-start text-sm font-medium text-slate-300">{t.leads.actions}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {leads.map((lead) => (
                        <tr key={lead._id} className="hover:bg-slate-700/20">
                          <td className="px-4 py-3 text-sm text-white">{lead.name}</td>
                          <td className="px-4 py-3 text-sm text-slate-300" dir="ltr">{lead.email}</td>
                          <td className="px-4 py-3 text-sm text-slate-300" dir="ltr">{lead.phone || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {lead.isSubscribed && (
                                <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded">{t.leads.subscribed}</span>
                              )}
                              {lead.hasReplied && (
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">{t.leads.replied}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => extendCampaign(lead._id, 7)}
                                className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs rounded hover:bg-blue-500/30 transition-all"
                              >
                                {t.leads.extend}
                              </button>
                              <button
                                onClick={() => deleteLead(lead._id)}
                                className="px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded hover:bg-red-500/30 transition-all"
                              >
                                {t.leads.delete}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {leadsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-700/50">
                  <button
                    onClick={() => {
                      setLeadsPage(Math.max(1, leadsPage - 1));
                      loadLeads(Math.max(1, leadsPage - 1), searchQuery);
                    }}
                    disabled={leadsPage === 1}
                    className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
                  >
                    {t.common.previous}
                  </button>
                  <span className="text-slate-400 text-sm">
                    {t.common.page} {leadsPage} {t.common.of} {leadsTotalPages}
                  </span>
                  <button
                    onClick={() => {
                      setLeadsPage(Math.min(leadsTotalPages, leadsPage + 1));
                      loadLeads(Math.min(leadsTotalPages, leadsPage + 1), searchQuery);
                    }}
                    disabled={leadsPage === leadsTotalPages}
                    className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
                  >
                    {t.common.next}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Unknown Replies Tab */}
        {activeTab === 'unknownReplies' && (
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
            <div className="p-4 border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-white">{t.replies.title}</h3>
            </div>
            {unknownReplies.length === 0 ? (
              <p className="p-6 text-center text-slate-400">{t.replies.noReplies}</p>
            ) : (
              <div className="divide-y divide-slate-700/50">
                {unknownReplies.map((reply) => (
                  <div key={reply._id} className={`p-4 ${!reply.isRead ? 'bg-blue-500/5' : ''}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-medium">{reply.fromEmail}</span>
                          {!reply.isRead && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">NEW</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-300 mb-1"><strong>{t.settings.subject}:</strong> {reply.subject}</p>
                        <p className="text-sm text-slate-400 line-clamp-2">{reply.body}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {new Date(reply.createdAt).toLocaleString(lang === 'ar' ? 'ar-SA' : lang === 'de' ? 'de-DE' : 'en-US')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!reply.isRead && (
                          <button
                            onClick={() => markReplyAsRead(reply._id)}
                            className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs rounded hover:bg-blue-500/30 transition-all"
                          >
                            {t.replies.markRead}
                          </button>
                        )}
                        {!reply.isResolved && (
                          <button
                            onClick={() => resolveReply(reply._id)}
                            className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded hover:bg-emerald-500/30 transition-all"
                          >
                            {t.replies.resolve}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Broadcast Tab */}
        {activeTab === 'broadcast' && (
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
            <h3 className="text-lg font-semibold text-white mb-2">{t.broadcast.title}</h3>
            <p className="text-sm text-slate-400 mb-6">{t.broadcast.description}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.broadcast.subject}</label>
                <input
                  type="text"
                  value={broadcastSubject}
                  onChange={(e) => setBroadcastSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.broadcast.body}</label>
                <textarea
                  value={broadcastBody}
                  onChange={(e) => setBroadcastBody(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              
              {/* File Attachment */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.broadcast.attachment}</label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    id="broadcast-file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setBroadcastAttachment(file);
                        setBroadcastAttachmentName(file.name);
                      }
                    }}
                    className="hidden"
                    accept="image/*,video/*,.pdf,.doc,.docx"
                  />
                  <label
                    htmlFor="broadcast-file"
                    className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg cursor-pointer text-sm"
                  >
                    {t.whatsapp?.uploadFile || 'Upload File'}
                  </label>
                  {broadcastAttachmentName && (
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400 text-sm">{broadcastAttachmentName}</span>
                      <button
                        onClick={() => {
                          setBroadcastAttachment(null);
                          setBroadcastAttachmentName('');
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        X
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={sendBroadcast}
                disabled={loading || !broadcastSubject || !broadcastBody}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50"
              >
                {loading ? t.broadcast.sending : `${t.broadcast.send} (${stats.subscribedLeads})`}
              </button>
            </div>
          </div>
        )}

        {/* WhatsApp Tab */}
        {activeTab === 'whatsapp' && (
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t.whatsapp.title}</h2>
                <p className="text-sm text-slate-400">{t.whatsapp.settings}</p>
              </div>
            </div>
            <WhatsAppPanel token={token} t={t} showNotification={showNotification} />
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;
